/*
 * Candidate-plan review — the approval moment.
 *
 * When the agent proposes a test (or a repair), a human decides whether it
 * becomes the committed plan. This page shows the candidate's steps and the
 * locators it chose, and the explainable diff against the committed plan.
 *
 * The decision itself is deliberately not a button here. The verifier never
 * writes to a git repository — its API has no accept or reject route — so
 * acceptance is a local operation: `blume-tests accept` (or the
 * `accept_candidate_plan` MCP tool), guarded by --expected-scenario-hash.
 * This page's final step is handing that command over, hash filled in.
 * There is no reject operation anywhere in the toolchain: a candidate that
 * is never accepted simply expires with its run.
 */
import type {
  ActionPlan,
  ActionPlanStep,
  CompiledAction,
  CompiledLocator,
  LocatorHint,
  WebTestRun
} from '@blume/web-test-model'
import { Check, Copy, FileDiff, Minus, Pencil, Plus } from 'lucide-react'
import { useState } from 'react'

import { Skeleton } from '@/components/ui/skeleton'
import type { PlanDiffEntry } from '@/design/fixture-candidates'
import { originLabel } from '@/design/format'
import { toneText } from '@/design/status'
import { cn } from '@/lib/utils'
import type { CandidateReviewFeed } from '@/live/use-candidate'

import { BackLink, EmptyState, MetaLine, SectionLabel } from './parts'

/* ------------------------------------------------------------------ */
/* Locator language                                                    */
/* ------------------------------------------------------------------ */

export function formatLocatorHint(hint: LocatorHint): string {
  if (hint.by === 'role') return `role=${hint.role}${hint.name ? ` “${hint.name}”` : ''}`
  if (hint.by === 'label') return `label “${hint.value}”`
  if (hint.by === 'placeholder') return `placeholder “${hint.value}”`
  if (hint.by === 'text') return `text “${hint.value}”`
  if (hint.by === 'test-id') return `test-id ${hint.value}`
  return `css ${hint.value}`
}

function actionSentence(action: CompiledAction): string {
  if (action.kind === 'navigate') return `navigate ${action.route}`
  if (action.kind === 'wait-route') return `wait for route ${action.route}`
  if (action.kind === 'wait-network-idle') return 'wait for network idle'
  if (action.kind === 'press') return `press ${action.key}`
  if (action.kind === 'drag') return 'drag'
  if (action.kind === 'fill') return 'fill'
  if (action.kind === 'select') return 'select'
  if (action.kind === 'wait-text') return `wait for text “${action.value}”`
  return action.kind.replace('wait-', 'wait until ')
}

function actionTarget(action: CompiledAction): CompiledLocator | undefined {
  if ('target' in action && action.target !== undefined) return action.target
  return undefined
}

/** A locator with its fallbacks and the compiler's rationale — reviewable. */
function LocatorBlock({ locator }: { locator: CompiledLocator }) {
  return (
    <div className="mt-1.5 space-y-1">
      <p className="font-mono text-[13px]">{formatLocatorHint(locator.primary)}</p>
      {locator.fallbacks.length > 0 ? (
        <p className="font-mono text-[13px] text-muted-foreground">
          {locator.fallbacks
            .map((fallback) => `fallback: ${formatLocatorHint(fallback)}`)
            .join(' · ')}
        </p>
      ) : null}
      <p className="text-[13px] leading-5 text-muted-foreground">{locator.rationale}</p>
    </div>
  )
}

function CandidateStep({ step, index }: { step: ActionPlanStep; index: number }) {
  const target = step.action ? actionTarget(step.action) : undefined
  return (
    <li className="border-t py-4 first:border-t-0">
      <div className="flex items-baseline gap-3">
        <span
          aria-hidden
          className="w-6 shrink-0 text-right font-mono text-xs text-muted-foreground tabular-nums"
        >
          {index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{step.description}</p>
          <span className="mt-0.5 block">
            <MetaLine
              items={[
                step.action ? actionSentence(step.action) : 'assertion only',
                step.assertions.length > 0
                  ? `${step.assertions.length} assertion${step.assertions.length > 1 ? 's' : ''}`
                  : undefined,
                `${step.timeoutMs / 1_000}s timeout`
              ]}
            />
          </span>
          {target ? <LocatorBlock locator={target} /> : null}
          {step.assertions.map((assertion, assertionIndex) =>
            assertion.target ? (
              <div key={assertionIndex} className="mt-2">
                <p className="text-xs font-medium tracking-[0.08em] text-muted-foreground uppercase">
                  Asserts
                </p>
                <LocatorBlock locator={assertion.target} />
              </div>
            ) : null
          )}
        </div>
      </div>
    </li>
  )
}

/* ------------------------------------------------------------------ */
/* Diff presentation                                                   */
/* ------------------------------------------------------------------ */

const diffMeta: Record<
  PlanDiffEntry['kind'],
  { label: string; icon: typeof Plus; tone: 'info' | 'warn' | 'neutral' }
> = {
  added: { label: 'Added', icon: Plus, tone: 'info' },
  removed: { label: 'Removed', icon: Minus, tone: 'warn' },
  changed: { label: 'Changed', icon: Pencil, tone: 'warn' },
  unchanged: { label: 'Unchanged', icon: Check, tone: 'neutral' }
}

function DiffRow({ entry }: { entry: PlanDiffEntry }) {
  const meta = diffMeta[entry.kind]
  const Icon = meta.icon
  const quiet = meta.tone === 'neutral'
  return (
    <li className="border-t py-3 first:border-t-0">
      <div className="flex items-baseline gap-2.5">
        <Icon
          aria-hidden
          className={cn(
            'size-3.5 shrink-0 self-center',
            quiet ? 'text-muted-foreground' : toneText[meta.tone]
          )}
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm">
            <span
              className={cn('font-medium', quiet ? 'text-muted-foreground' : toneText[meta.tone])}
            >
              {meta.label}
            </span>{' '}
            {entry.description}
          </p>
          {entry.before !== undefined ? (
            <p className="mt-1 font-mono text-[13px] text-muted-foreground">was: {entry.before}</p>
          ) : null}
          {entry.after !== undefined && entry.kind !== 'unchanged' ? (
            <p className="mt-0.5 font-mono text-[13px]">now: {entry.after}</p>
          ) : null}
          {entry.reason ? (
            <p className="mt-1 max-w-prose text-[13px] leading-5 text-muted-foreground">
              {entry.reason}
            </p>
          ) : null}
        </div>
      </div>
    </li>
  )
}

/* ------------------------------------------------------------------ */
/* Acceptance hand-off — review here, accept where the repo is         */
/* ------------------------------------------------------------------ */

const SCENARIO_HASH_PATTERN = /^[a-f0-9]{64}$/

/**
 * The exact local command that performs the accept, hash guard filled in.
 * Returns undefined when the candidate's scenario hash is unusable, so the
 * page can be honest about it instead of emitting a command that will fail.
 */
export function acceptanceCommand(runId: string, candidate: ActionPlan): string | undefined {
  const hash = candidate.scenarioHash.trim()
  if (!SCENARIO_HASH_PATTERN.test(hash)) return undefined
  return `blume-tests accept ${runId} --target ${candidate.target} --expected-scenario-hash ${hash}`
}

/**
 * The copyable command block. The copy button carries a written label —
 * "Copy command" flipping to a visible "Copied" confirmation — and a polite
 * status announcement; a clipboard failure is stated, never swallowed.
 */
function CommandBlock({ command }: { command: string }) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
  return (
    <div className="rounded-md border">
      <div className="flex items-center justify-between gap-3 border-b px-3 py-1.5">
        <span className="text-xs font-medium tracking-[0.08em] text-muted-foreground uppercase">
          Run in your checkout
        </span>
        <button
          type="button"
          aria-label="Copy acceptance command"
          onClick={async () => {
            const clipboard = navigator.clipboard
            if (!clipboard) {
              setCopyState('failed')
              return
            }
            try {
              await clipboard.writeText(command)
              setCopyState('copied')
              window.setTimeout(() => setCopyState('idle'), 2_000)
            } catch {
              setCopyState('failed')
            }
          }}
          className={cn(
            'inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
            copyState === 'copied'
              ? toneText.pass
              : 'text-muted-foreground hover:bg-accent hover:text-foreground'
          )}
        >
          {copyState === 'copied' ? (
            <Check aria-hidden className="size-3.5" />
          ) : (
            <Copy aria-hidden className="size-3.5" />
          )}
          {copyState === 'copied' ? 'Copied' : 'Copy command'}
        </button>
      </div>
      <pre className="overflow-x-auto px-3 py-2.5 font-mono text-[13px] leading-5">
        <code>{command}</code>
      </pre>
      <p role="status" className="sr-only">
        {copyState === 'copied' ? 'Acceptance command copied to the clipboard' : ''}
      </p>
      {copyState === 'failed' ? (
        <p className={cn('border-t px-3 py-1.5 text-[13px]', toneText.warn)}>
          Copying failed in this browser — select the command text and copy it manually.
        </p>
      ) : null}
    </div>
  )
}

/**
 * The final step of the review. Deliberately not a button: the verifier
 * never writes to the repository, so acceptance runs locally with the
 * scenario-hash guard. This panel hands that command over, ready to paste.
 */
function AcceptancePanel({
  runId,
  run,
  candidate,
  mode
}: {
  runId: string
  run: WebTestRun | undefined
  candidate: ActionPlan
  mode: CandidateReviewFeed['mode']
}) {
  const stillProving = run !== undefined && run.state !== 'terminal'
  if (stillProving) {
    return (
      <p className="mt-4 max-w-prose text-sm leading-6 text-muted-foreground">
        This candidate is still being proven — the acceptance hand-off appears once the run finishes
        and the verifier asks for review.
      </p>
    )
  }

  const command = acceptanceCommand(runId, candidate)
  return (
    <div className="mt-4 max-w-2xl space-y-4">
      <p className="max-w-prose text-sm leading-6">
        <span className="font-medium">
          Accepting makes this the committed test for{' '}
          <span className="font-mono">{candidate.scenarioId}</span>
        </span>{' '}
        <span className="text-muted-foreground">
          — every future run replays it deterministically. The verifier never writes to your
          repository, so acceptance happens on your machine, where the repo is.
        </span>
      </p>

      {command !== undefined ? (
        <CommandBlock command={command} />
      ) : (
        <div className="rounded-md border p-4">
          <p className={cn('text-sm leading-6', toneText.warn)}>
            The candidate did not carry a usable scenario hash, and acceptance requires one.
          </p>
          <p className="mt-1.5 max-w-prose text-sm leading-6 text-muted-foreground">
            Read it locally with{' '}
            <code className="font-mono text-[13px]">
              blume-tests scenario {candidate.scenarioId}
            </code>{' '}
            (the <code className="font-mono text-[13px]">scenarioHash</code> field), then run{' '}
            <code className="font-mono text-[13px]">
              blume-tests accept {runId} --target {candidate.target} --expected-scenario-hash
              &lt;scenario-hash&gt;
            </code>
            .
          </p>
        </div>
      )}

      <ul className="max-w-prose space-y-2 text-[13px] leading-5 text-muted-foreground">
        <li>
          <span className="font-mono">--expected-scenario-hash</span> pins the write to the exact
          scenario you just reviewed — if the scenario file has changed since this run, acceptance
          refuses instead of committing a stale plan.
        </li>
        <li>
          Agent-authored runs need no keyboard: the agent can accept through the{' '}
          <span className="font-mono">accept_candidate_plan</span> MCP tool, under the same hash
          guard.
        </li>
        <li>
          Not convinced? Do nothing. There is no reject step — a candidate that is never accepted
          expires with its run, and the committed plan stays exactly as it is.
        </li>
        {mode === 'fixture' ? (
          <li>Demo data — the run id and hash in this command are fixture values.</li>
        ) : null}
      </ul>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export function CandidateReviewPage({
  runId,
  run,
  review
}: {
  runId: string
  run: WebTestRun | undefined
  review: CandidateReviewFeed
}) {
  const repair = run?.attempts.some(
    (attempt) => attempt.purpose === 'repair-discovery' || attempt.purpose === 'repair-validation'
  )
  return (
    <>
      <BackLink href={`#/runs/${encodeURIComponent(runId)}`} label="Back to run" />

      <header className="mb-8 max-w-3xl">
        <div className="flex items-center gap-3">
          <FileDiff aria-hidden className={cn('size-7 shrink-0', toneText.warn)} />
          <h1 className="text-[28px] font-semibold tracking-tight text-balance">
            {repair ? 'Review the proposed repair' : 'Review the proposed test'}
          </h1>
        </div>
        {run ? (
          <div className="mt-2">
            <MetaLine
              items={[
                <span key="scenario" className="font-mono">
                  {run.scenarioId}
                </span>,
                `proposed by ${run.initiatedBy} over ${originLabel[run.origin] ?? run.origin}`,
                <a
                  key="run"
                  href={`#/runs/${encodeURIComponent(run.id)}`}
                  className="underline underline-offset-2 hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
                >
                  from run {run.id}
                </a>
              ]}
            />
          </div>
        ) : null}
        <p className="mt-4 max-w-prose text-[15px] leading-7 text-muted-foreground">
          {repair
            ? 'The agent believes the test drifted, not the product, and validated a repaired plan. Accepting commits the repair; left unaccepted, the current plan stands and the failure stays open.'
            : 'The agent explored the scenario, compiled this plan from its successful trace, and proved it with clean replays. Accepting commits it as the trusted test.'}
        </p>
      </header>

      <ReviewBody runId={runId} run={run} review={review} />
    </>
  )
}

function ReviewBody({
  runId,
  run,
  review
}: {
  runId: string
  run: WebTestRun | undefined
  review: CandidateReviewFeed
}) {
  const { state } = review
  if (state.kind === 'loading') {
    return (
      <div aria-label="Loading candidate plan" className="max-w-3xl space-y-3">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="h-5 w-3/5" />
        <p className="text-[13px] text-muted-foreground">Loading the candidate plan…</p>
      </div>
    )
  }
  if (state.kind === 'unauthorized') {
    return (
      <EmptyState>
        You don’t have permission to review this candidate — {state.message}. Sign in to the
        verifier and reload.
      </EmptyState>
    )
  }
  if (state.kind === 'error') {
    return (
      <EmptyState>
        The candidate plan could not be loaded — {state.message}. The committed plan is unchanged.
      </EmptyState>
    )
  }
  if (state.kind === 'empty') {
    return (
      <EmptyState>
        No candidate plan is attached to run <span className="font-mono">{runId}</span>. It may have
        already been accepted locally, or expired with the run.
      </EmptyState>
    )
  }

  const { candidate, diff, rawDiff } = state
  return (
    <div className="max-w-3xl space-y-12">
      <section aria-label="Proposed steps and locators">
        <SectionLabel>Proposed steps &amp; locators</SectionLabel>
        <p className="mb-2 text-sm text-muted-foreground">
          {candidate.steps.length} steps · compiled by {candidate.compilerVersion} · locators are
          accessibility-first by contract.
        </p>
        <ol>
          {candidate.steps.map((step, index) => (
            <CandidateStep key={step.id} step={step} index={index} />
          ))}
        </ol>
      </section>

      <section aria-label="Changes against the committed plan">
        <SectionLabel>Against the committed plan</SectionLabel>
        {diff ? (
          <>
            <p className="mb-2 text-sm text-muted-foreground">
              {diff.baselinePlanHash
                ? 'What this candidate changes, and why.'
                : 'This scenario has no committed plan yet — every step is new.'}
            </p>
            <ul>
              {diff.entries.map((entry) => (
                <DiffRow key={`${entry.kind}-${entry.stepId}`} entry={entry} />
              ))}
            </ul>
          </>
        ) : rawDiff !== undefined ? (
          <>
            <p className="mb-2 text-sm text-muted-foreground">
              The verifier sent a diff this dashboard cannot explain yet — shown raw rather than
              reshaped.
            </p>
            <pre className="max-w-full overflow-x-auto rounded-md bg-muted px-3 py-2 font-mono text-xs leading-5">
              {JSON.stringify(rawDiff, null, 2)}
            </pre>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">The verifier reported no diff.</p>
        )}
      </section>

      <section aria-label="Accepting this plan">
        <SectionLabel>Accepting this plan</SectionLabel>
        <AcceptancePanel runId={runId} run={run} candidate={candidate} mode={review.mode} />
      </section>
    </div>
  )
}
